/**
 * Utility functions for formatting dates in the user's local timezone
 */

/**
 * Get the user's timezone abbreviation (e.g., "PST", "CET", "GMT+2")
 */
export const getUserTimezone = (): string => {
  try {
    // Get the timezone name
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Get the short timezone abbreviation using the user's locale
    const date = new Date();
    const shortTz =
      date
        .toLocaleTimeString(undefined, {
          timeZoneName: "short",
        })
        .split(" ")
        .pop() || "";

    return shortTz;
  } catch (error) {
    return "Local Time";
  }
};

/**
 * Get the full timezone name (e.g., "Europe/Oslo", "America/New_York")
 */
export const getUserTimezoneName = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (error) {
    return "Local";
  }
};

/**
 * Format a timestamp to the user's local timezone with date and time
 */
export const formatTimestamp = (timestamp: string | Date | number): string => {
  const date =
    typeof timestamp === "string"
      ? new Date(timestamp)
      : typeof timestamp === "number"
      ? new Date(timestamp)
      : timestamp;

  return date.toLocaleString("default", {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/**
 * Format a timestamp with timezone indicator
 */
export const formatTimestampWithTz = (timestamp: string | Date | number): string => {
  const formatted = formatTimestamp(timestamp);
  const tz = getUserTimezone();
  return `${formatted} (${tz})`;
};

/**
 * Format time only from a time string (HH:MM:SS)
 */
export const formatTimeOnly = (time: string): string => {
  const d = new Date();
  const [h, m, s] = time.split(":").map(Number);
  d.setHours(h, m, s || 0);
  if (isNaN(d.getTime())) return "Invalid Time";
  return d.toLocaleTimeString("default", { hour: "2-digit", minute: "2-digit" });
};

/**
 * Format a UTC time string to local time display
 * @param utcTime - Time string in format "HH:MM:SS" or "HH:MM" (UTC)
 * @returns Formatted local time string
 */
export const formatUTCTimeToLocal = (utcTime: string | null): string => {
  if (!utcTime) return "";

  const [hours, minutes] = utcTime.split(":").map(Number);
  const now = new Date();
  now.setUTCHours(hours, minutes, 0, 0);

  return now.toLocaleTimeString("default", { hour: "2-digit", minute: "2-digit" });
};

/**
 * Format a date to user's local date only (no time)
 */
export const formatDateOnly = (timestamp: string | Date | number): string => {
  const date =
    typeof timestamp === "string"
      ? new Date(timestamp)
      : typeof timestamp === "number"
      ? new Date(timestamp)
      : timestamp;

  return date.toLocaleDateString("default", {
    year: "numeric",
    month: "numeric",
    day: "numeric",
  });
};

/**
 * Format a date to user's local date with timezone
 */
export const formatDateOnlyWithTz = (timestamp: string | Date | number): string => {
  const formatted = formatDateOnly(timestamp);
  const tz = getUserTimezone();
  return `${formatted} (${tz})`;
};

/**
 * Convert a local time string (HH:MM) to UTC time string (HH:MM:SS)
 * @param localTime - Time string in format "HH:MM"
 * @returns UTC time string in format "HH:MM:SS"
 */
export const convertLocalTimeToUTC = (localTime: string): string => {
  if (!localTime) return "";

  const [hours, minutes] = localTime.split(":").map(Number);
  const now = new Date();
  now.setHours(hours, minutes, 0, 0);

  const utcHours = now.getUTCHours().toString().padStart(2, "0");
  const utcMinutes = now.getUTCMinutes().toString().padStart(2, "0");

  return `${utcHours}:${utcMinutes}:00`;
};

/**
 * Convert a UTC time string (HH:MM:SS or HH:MM) to local time string (HH:MM)
 * @param utcTime - Time string in format "HH:MM:SS" or "HH:MM"
 * @returns Local time string in format "HH:MM"
 */
export const convertUTCTimeToLocal = (utcTime: string): string => {
  if (!utcTime) return "";

  const [hours, minutes] = utcTime.split(":").map(Number);
  const now = new Date();
  now.setUTCHours(hours, minutes, 0, 0);

  const localHours = now.getHours().toString().padStart(2, "0");
  const localMinutes = now.getMinutes().toString().padStart(2, "0");

  return `${localHours}:${localMinutes}`;
};
